import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { updateBook } from "../slice/bookSlice";
import { fetchRatings } from "../slice/ratingSlice";
import useLoginCheck from "../util/useLoginCheck";
import { formatAuthors } from "../util/books";

const EditBook = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const initialBook = location.state?.book;

  const isLoading = useSelector((state) => state.books.isLoading);
  const reduxRatings = useSelector((state) => state.ratings.ratings);
  const [editableBook, setEditableBook] = useState({
    sid: "",
    title: "",
    subTitle: "",
    dateRead: "",
    rating: -1,
    review: "",
    quotes: [],
  });
  const [savingBook, setSavingBook] = useState(false);
  const [ratings, setRatings] = useState([]);

  useLoginCheck();

  useEffect(() => {
    dispatch(fetchRatings());
  }, [dispatch]);

  useEffect(() => {
    if (initialBook) {
      setEditableBook(initialBook);
    }
  }, [initialBook]);

  useEffect(() => {
    if (reduxRatings?.length > 0) {
      setRatings(reduxRatings);
    }
  }, [reduxRatings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSavingBook(true);
      const postData = {
        sid: editableBook?.sid,
        title: editableBook?.title,

        dateRead: editableBook?.dateRead,
        rating: editableBook?.rating,
        review: editableBook?.review,
        quoteUpdates: editableBook?.quotes,
      };
      const resultAction = await dispatch(updateBook(postData));
      if (updateBook.fulfilled.match(resultAction)) {
        navigate(`/books/${editableBook?.sid}`);
      }
    } catch (error) {
      console.log(
        "error updating book, message: ",
        error?.response?.data?.errorMessage
      );
    } finally {
      setSavingBook(false);
    }
  };

  const setDateRead = (dateRead) => {
    setEditableBook((prev) => ({ ...prev, dateRead }));
  };

  const handleRatingSelected = (e) => {
    const value = parseInt(e.target.value, 10);
    const selectedRating = ratings.find((r) => r.value === value);
    setEditableBook((prev) => ({
      ...prev,
      rating: selectedRating,
      ratingId: selectedRating?.value,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditableBook((prev) => ({ ...prev, [name]: value }));
  };

  const updateQuote = (index, text) => {
    setEditableBook((prev) => ({
      ...prev,
      quotes: prev?.quotes?.map((q, i) =>
        i === index ? { ...q, quote: text } : q
      ),
    }));
  };

  const removeQuote = (index) => {
    setEditableBook((prev) => ({
      ...prev,
      quotes: prev?.quotes?.filter((_, i) => i !== index),
    }));
  };

  const addQuote = () => {
    setEditableBook((prev) => ({
      ...prev,
      quotes: [
        ...prev.quotes,
        { id: null, quote: "", bookSid: prev.sid },
      ],
    }));
  };

  const fieldLabelClasses = "block text-sm font-medium text-stone-500 mb-1";
  const inputClasses =
    "w-full border border-stone-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600";

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      <form onSubmit={handleSubmit} className="card p-4 md:p-6 space-y-4">
        <div>
          <span className={fieldLabelClasses}>Title</span>
          <p className="text-stone-800">
            {editableBook?.title}
            {editableBook?.subTitle ? `: ${editableBook.subTitle}` : ""}
          </p>
        </div>
        <div>
          <span className={fieldLabelClasses}>Authors</span>
          <p className="text-stone-800">
            {formatAuthors(editableBook?.authors)}
          </p>
        </div>
        <div>
          <label className={fieldLabelClasses}>Rating</label>
          {editableBook?.rating !== undefined && reduxRatings?.length > 0 && (
            <select
              value={editableBook?.rating?.value}
              onChange={handleRatingSelected}
              className={inputClasses}
            >
              <option value="-1" disabled>
                Rating
              </option>
              {ratings.map((r, index) => (
                <option key={index} value={r.value}>
                  {r.description}
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className={fieldLabelClasses}>Date Read</label>
          <Flatpickr
            className={inputClasses}
            value={editableBook?.dateRead}
            onChange={(selectedDates) => setDateRead(selectedDates[0])}
            options={{ dateFormat: "Y-m-d" }}
          />
        </div>
        <div>
          <label className={fieldLabelClasses}>Review</label>
          <textarea
            placeholder="Review"
            name="review"
            value={editableBook?.review}
            onChange={handleChange}
            required
            className={`${inputClasses} min-h-[120px]`}
          />
        </div>
        <div>
          <span className={fieldLabelClasses}>Quotes</span>
          <div className="space-y-2">
            {editableBook?.quotes?.map((quote, index) => (
              <div key={index} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  className={`${inputClasses} flex-1`}
                  placeholder="Quote"
                  value={quote?.quote}
                  onChange={(e) => updateQuote(index, e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removeQuote(index)}
                  className="btn-secondary shrink-0"
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={addQuote} className="btn-secondary">
              Add Quote
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={savingBook || isLoading}
            className="btn-primary"
          >
            {savingBook ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditBook;
